import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import type { ParticipantProfile } from '../model/types'

/**
 * Профиль участника меняет не только он сам: заявку на смену города или страны
 * одобряет администратор из своей сессии. Поэтому глобальные 5 минут кеша здесь
 * не подходят — профиль перечитывается при каждом заходе на экран и при
 * возврате на вкладку. Проверено на стенде 2026-10-10: сразу после одобрения
 * GET отдаёт новый город, а экран держал старый до перезагрузки.
 *
 * Шапка держит тот же ключ, поэтому берёт эти же настройки, а не свои.
 */
export const participantProfileQuery = queryOptions({
  queryKey: QUERY_CACHE_KEYS.profile.participant,
  queryFn: () =>
    apiClient.get<ParticipantProfile>(endpoints.profile.participant).then((r) => r.data),
  staleTime: 0,
  refetchOnWindowFocus: true,
})

export function useParticipantProfile() {
  return useQuery(participantProfileQuery)
}

interface ParticipantProfilePatch {
  full_name_ru?: string
  full_name_en?: string
  country?: string
  /** City primary key. */
  city?: number | null
  /** Free-text city name, sent only when the "Other" city row is chosen. */
  other?: string
  school?: string
  /** Grade number 1..12 — the backend rejects GradeCode words like "twelfth". */
  grade?: number | null
  parent?: {
    name_ru: string | null
    name_en: string | null
    phone_number: string | null
  }
  teacher?: {
    name_ru: string | null
    name_en: string | null
    phone_number: string | null
  }
  /** id партнёра; `null` — убрать; поле опущено — оставить как есть. */
  representative_id?: number | null
}

/**
 * `silentWhen` — ошибки, которые вызывающий объясняет сам (у поля формы):
 * для них общий тост не показывается.
 */
export function useUpdateParticipantProfile(options?: {
  silentWhen?: (error: unknown) => boolean
}) {
  const qc = useQueryClient()
  return useMutation({
    meta: options?.silentWhen ? { silentWhen: options.silentWhen } : undefined,
    mutationFn: (data: FormData | ParticipantProfilePatch) =>
      apiClient.patch(endpoints.profile.participant, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.profile.participant }),
  })
}
